import sortBy from "lodash.sortby";
import React, { useEffect, useMemo } from "react";
import QRCode from "react-qr-code";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgDialog, IgSelect, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListDto,
  ExperienceListVariationDto,
  ExperiencePageTargetingDto,
  OrganizationDto,
  RedirectDto,
} from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";

import { getDialog, isDialogOpen, setDialog } from "../../store/slices/global";
import { ClipboardCopy } from "../../utils/ClipboardCopy";
import {
  generatePreviewLink,
  getPreviewThemeId,
} from "../../utils/generatePreviewLink";

interface PreviewVariationCodeProps {
  organization: OrganizationDto;
  variations: ExperienceListVariationDto[];
  experience: ExperienceListDto;
  redirects: RedirectDto[];
  pageTargeting: ExperiencePageTargetingDto[];
}

export const PreviewVariationCode = ({
  experience,
  organization,
  variations,
  redirects,
  pageTargeting,
}: PreviewVariationCodeProps) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen("previewTestGroupCode"));

  const dialog = useSelector(getDialog("previewTestGroupCode"));

  const handleClose = () => {
    dispatch(setDialog({ dialog: "previewTestGroupCode", open: false }));
  };

  const [selectedTestVariation, setSelectedTestVariation] =
    useImmer<ExperienceListVariationDto>(variations[0]);

  useEffect(() => {
    setSelectedTestVariation(variations[0]);
  }, [experience.id]);

  const codeUrl = useMemo(() => {
    return generatePreviewLink({
      organization,
      experience,
      variationId: selectedTestVariation?.id,
      previewThemeId:
        dialog.props?.themeId ||
        getPreviewThemeId(redirects.filter((redirect) => redirect.isThemeTest)),
      redirects,
      pageTargeting,
      useOriginUrl: true,
    });
  }, [organization, experience, selectedTestVariation, dialog.props?.themeId]);

  if (!variations?.length) {
    return null;
  }

  return (
    <IgDialog
      title={`Preview ${selectedTestVariation?.name}`}
      handleClose={handleClose}
      showDialog={isOpen}
      hideActions={true}
      showCloseIcon={true}
      dialogHeight={"fit-contents"}
      subtitle={
        <Grid
          item
          container
          justifyContent={"center"}
          marginBottom={"20px"}
          rowSpacing={2}
        >
          <Grid item>
            {/* @ts-ignore */}
            <QRCode
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              value={codeUrl}
              viewBox={`0 0 256 256`}
            />
          </Grid>
          <IgTypography sx={{ marginY: "10px" }}>
            This will open a preview of the '{selectedTestVariation?.name}' test
            group. To preview a different test group, use the dropdown below.
          </IgTypography>
          <IgSelect
            size={"small"}
            value={selectedTestVariation?.id || variations[0].id}
            onChange={(e) =>
              setSelectedTestVariation(
                variations.find(
                  (variation) => variation.id === e.target.value
                ) || variations[0]
              )
            }
          >
            {sortBy(variations, "order").map((variation, index) => (
              <MenuItem value={variation.id} key={index}>
                {variation?.name}
              </MenuItem>
            ))}
          </IgSelect>
          <ClipboardCopy
            codeFont={false}
            copyTextSx={{ fontSize: "14px", fontWeight: 600 }}
            copyText={codeUrl}
            showUrl={false}
            direction={"column"}
            copyButtonText={{ copy: "Copy Mobile URL", copied: "Copied!" }}
          />
        </Grid>
      }
    ></IgDialog>
  );
};
